// DNA CONNECT - Boletim Premium
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  GraduationCap, BookOpen, Award, TrendingUp, TrendingDown, Minus,
  AlertCircle, CheckCircle, Loader2, Info, Printer, ChevronDown, ChevronUp,
  Sparkles, Target, Eye, HelpCircle, BarChart3, User, Calendar, Hash,
  PenTool, Heart, Lightbulb, ClipboardList, Download
} from 'lucide-react';

const API_URL = window.location.origin;

// ── Helpers ────────────────────────────────
const fmtNota = (v) => (v !== null && v !== undefined) ? Number(v).toFixed(1) : null;
const arred05 = (v) => (v === null || v === undefined) ? null : Math.round(v * 2) / 2;

const getNotaClass = (nota) => {
  if (nota === null || nota === undefined) return 'text-slate-400';
  if (nota >= 7) return 'text-emerald-600';
  if (nota >= 6) return 'text-teal-600';
  if (nota >= 5) return 'text-amber-600';
  return 'text-rose-600';
};

const getSituacaoBadge = (situacao) => {
  const map = {
    'Aprovado': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Destaque': 'bg-teal-100 text-teal-800 border-teal-200',
    'Em curso': 'bg-blue-100 text-blue-800 border-blue-200',
    'Cursando': 'bg-blue-100 text-blue-800 border-blue-200',
    'Rec. Final': 'bg-rose-100 text-rose-800 border-rose-200',
    'Recuperacao': 'bg-amber-100 text-amber-800 border-amber-200',
    'Reprovado': 'bg-red-100 text-red-800 border-red-200',
  };
  return map[situacao] || 'bg-slate-100 text-slate-700 border-slate-200';
};

// ── SIGLAS (Legend) ────────────────────────────────
const SIGLAS = [
  { sigla: 'APA', nome: 'Avaliacao de Producao do Aluno', desc: 'Exercicios, atividades praticas e participacao em sala.' },
  { sigla: 'Prontidao', nome: 'Avaliacao de Prontidao', desc: 'Pontualidade, tarefas, organizacao e participacao.' },
  { sigla: 'Trabalho', nome: 'Trabalhos e Projetos', desc: 'Trabalhos individuais ou em grupo, pesquisas e apresentacoes.' },
  { sigla: 'Av. Trim.', nome: 'Avaliacao Trimestral', desc: 'Prova formal do conteudo estudado no trimestre.' },
  { sigla: 'Rec.', nome: 'Recuperacao', desc: 'Nota de recuperacao trimestral (media minima 6,0).' },
  { sigla: 'MF', nome: 'Media Final', desc: 'Media dos 3 trimestres, arredondada de 0,5 em 0,5.' },
];

// ── MAIN COMPONENT ────────────────────────────────
export default function BoletimPremium({ boletim, filhoAtual, selectedFilho }) {
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [showSubjectDetail, setShowSubjectDetail] = useState(null);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [showPrint, setShowPrint] = useState(false);
  // Family Assistant states
  const [assistantData, setAssistantData] = useState(null);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantSubject, setAssistantSubject] = useState(null);
  const [assistantTab, setAssistantTab] = useState('visao');

  // Load AI insight
  useEffect(() => {
    if (selectedFilho && boletim?.grades?.length > 0) {
      loadAiInsight();
    }
  }, [selectedFilho]);

  const loadAiInsight = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('familyToken');
      const res = await fetch(`${API_URL}/api/dnaconnect/pedagogico/boletim/${selectedFilho}/ai-insight?token=${token}`);
      if (res.ok) {
        const data = await res.json();
        setAiInsight(data);
      }
    } catch (e) {
      console.error('AI insight error:', e);
    } finally {
      setAiLoading(false);
    }
  };

  const openAssistant = async (subject) => {
    setAssistantSubject(subject);
    setAssistantTab('visao');
    setAssistantData(null);
    setAssistantLoading(true);
    try {
      const token = localStorage.getItem('familyToken');
      const res = await fetch(`${API_URL}/api/dnaconnect/ai/family-assistant/generate?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: selectedFilho, subject_id: subject.id, subject_name: subject.name, term: '' })
      });
      if (res.ok) setAssistantData(await res.json());
      else toast.error('Erro ao gerar plano');
    } catch { toast.error('Erro de conexao'); }
    finally { setAssistantLoading(false); }
  };

  // ── Data Processing ────────────────────────────────
  const processedData = useMemo(() => {
    if (!boletim?.subjects || !boletim?.grades) return { subjects: [], mediaGeral: null, totalFaltas: 0, situacaoGeral: '-', totalDisciplinas: 0 };

    const trimestres = ['1o Trimestre', '2o Trimestre', '3o Trimestre'];
    const subjectsData = boletim.subjects.map(subject => {
      const trims = {};
      trimestres.forEach(tri => {
        const notaFinal = boletim.grades.find(g =>
          g.subject_id === subject.id && g.period === tri && (g.grade_type === 'trimestral' || g.grade_type === 'final')
        );
        // Also try with accented version
        const notaFinalAcc = !notaFinal ? boletim.grades.find(g =>
          g.subject_id === subject.id && g.period === tri.replace('o ', 'º ') && (g.grade_type === 'trimestral' || g.grade_type === 'final')
        ) : null;
        trims[tri] = arred05((notaFinal || notaFinalAcc)?.grade_value ?? null);

        // Get partial grades for this trimester
        const parciais = boletim.grades.filter(g =>
          g.subject_id === subject.id &&
          (g.period === tri || g.period === tri.replace('o ', 'º ')) &&
          g.grade_type === 'parcial'
        );
        trims[`${tri}_parciais`] = {};
        parciais.forEach(p => {
          trims[`${tri}_parciais`][p.evaluation_type] = p.grade_value;
        });
      });

      const notasValidas = trimestres.map(t => trims[t]).filter(n => n !== null && n !== undefined);
      const mediaFinal = notasValidas.length === 3 ? arred05(notasValidas.reduce((a, b) => a + b, 0) / 3) : null;
      const mediaParcial = notasValidas.length > 0 ? arred05(notasValidas.reduce((a, b) => a + b, 0) / notasValidas.length) : null;

      let situacao = 'Cursando';
      if (mediaFinal !== null) {
        if (mediaFinal >= 8) situacao = 'Destaque';
        else if (mediaFinal >= 6) situacao = 'Aprovado';
        else situacao = 'Rec. Final';
      } else if (mediaParcial !== null) {
        if (mediaParcial >= 7) situacao = 'Destaque';
        else if (mediaParcial >= 6) situacao = 'Em curso';
        else situacao = 'Atencao';
      }

      // Count faltas from grades with falta info
      const faltasDisc = boletim.grades.filter(g => g.subject_id === subject.id && g.grade_type === 'falta').reduce((s, g) => s + (g.grade_value || 0), 0);

      return { ...subject, trims, mediaFinal, mediaParcial, situacao, faltas: faltasDisc, notasValidas };
    });

    const mediasValidas = subjectsData.map(s => s.mediaParcial || s.mediaFinal).filter(m => m !== null);
    const mediaGeral = mediasValidas.length > 0 ? arred05(mediasValidas.reduce((a, b) => a + b, 0) / mediasValidas.length) : null;
    const totalFaltas = subjectsData.reduce((s, d) => s + d.faltas, 0);
    const aprovados = subjectsData.filter(s => s.situacao === 'Aprovado' || s.situacao === 'Destaque').length;

    let situacaoGeral = 'Cursando';
    if (mediaGeral !== null) {
      if (mediaGeral >= 7) situacaoGeral = 'Destaque';
      else if (mediaGeral >= 6) situacaoGeral = 'Bom desempenho';
      else situacaoGeral = 'Atencao';
    }

    return { subjects: subjectsData, mediaGeral, totalFaltas, situacaoGeral, totalDisciplinas: subjectsData.length, aprovados };
  }, [boletim]);

  const student = boletim?.student || filhoAtual || {};

  // ── Loading State ────────────────────────────────
  if (!boletim) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-10 h-10 animate-spin text-purple-500 mb-4" />
        <p className="text-slate-500">Carregando boletim...</p>
      </div>
    );
  }

  // ── Empty State ────────────────────────────────
  if (!boletim.subjects?.length && !boletim.grades?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
        <BookOpen className="w-16 h-16 text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">Sem dados academicos</h3>
        <p className="text-slate-500 text-sm text-center max-w-md">Ainda nao ha notas ou disciplinas lancadas para este periodo.</p>
      </div>
    );
  }

  const toggleAccordion = (id) => setExpandedSubjects(prev => ({ ...prev, [id]: !prev[id] }));

  // ── PRINT VIEW ────────────────────────────────
  if (showPrint) {
    return (
      <div className="bg-white p-8 max-w-4xl mx-auto" data-testid="print-view">
        <style>{`@media print { body * { visibility: hidden; } [data-testid="print-view"], [data-testid="print-view"] * { visibility: visible; } [data-testid="print-view"] { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none !important; } }`}</style>
        <div className="text-center mb-6 border-b pb-4">
          <h1 className="text-2xl font-bold">Boletim Escolar</h1>
          <p className="text-sm text-slate-500">NOVO DNA - Educacao Inteligente</p>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
          <div><strong>Aluno:</strong> {student.name}</div>
          <div><strong>Turma:</strong> {student.class_name}</div>
          <div><strong>Ano:</strong> {boletim.year}</div>
        </div>
        <table className="w-full text-sm border-collapse border border-slate-300 mb-6">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-300 p-2 text-left">Disciplina</th>
              <th className="border border-slate-300 p-2 text-center">1T</th>
              <th className="border border-slate-300 p-2 text-center">2T</th>
              <th className="border border-slate-300 p-2 text-center">3T</th>
              <th className="border border-slate-300 p-2 text-center">MF</th>
              <th className="border border-slate-300 p-2 text-center">Situacao</th>
            </tr>
          </thead>
          <tbody>
            {processedData.subjects.map(s => (
              <tr key={s.id}>
                <td className="border border-slate-300 p-2">{s.name}</td>
                {['1o Trimestre', '2o Trimestre', '3o Trimestre'].map(t => (
                  <td key={t} className="border border-slate-300 p-2 text-center">{fmtNota(s.trims[t]) || '-'}</td>
                ))}
                <td className="border border-slate-300 p-2 text-center font-bold">{fmtNota(s.mediaFinal) || '-'}</td>
                <td className="border border-slate-300 p-2 text-center">{s.situacao}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {aiInsight?.resumo_geral && (
          <div className="mt-4 p-3 bg-slate-50 rounded text-sm">
            <p className="font-semibold mb-1">Resumo Inteligente:</p>
            <p className="text-slate-600">{aiInsight.resumo_geral}</p>
            <p className="text-xs text-slate-400 mt-2 italic">Este resumo e interpretativo e nao substitui os dados oficiais.</p>
          </div>
        )}
        <div className="mt-6 flex gap-4 no-print">
          <Button onClick={() => { setShowPrint(false); }} variant="outline">Voltar</Button>
          <Button onClick={() => window.print()} className="bg-purple-700 hover:bg-purple-800"><Printer className="w-4 h-4 mr-2" />Imprimir</Button>
        </div>
      </div>
    );
  }

  // ── MAIN RENDER ────────────────────────────────
  return (
    <div className="space-y-5" data-testid="boletim-premium">
      {/* ── Student Header ────────────────────────────── */}
      <div className="bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 rounded-2xl p-5 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-5 h-5 text-purple-300" />
              <span className="text-xs uppercase tracking-wider text-purple-300 font-semibold">Boletim Escolar {boletim?.year}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold">{student.name || 'Aluno'}</h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-purple-200">
              {student.registration_number && <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{student.registration_number}</span>}
              <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" />{student.class_name || '-'}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{boletim?.year}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-purple-200 hover:text-white hover:bg-white/10" onClick={() => setShowLegend(true)}>
              <HelpCircle className="w-4 h-4 mr-1" />Siglas
            </Button>
            <Button size="sm" variant="ghost" className="text-purple-200 hover:text-white hover:bg-white/10" data-testid="connect-boletim-oficial-pdf" onClick={() => {
              const token = localStorage.getItem('familyToken');
              window.open(`${API_URL}/api/dnaconnect/pedagogico/boletim/${selectedFilho}/pdf?token=${token}&year=${boletim?.year || new Date().getFullYear()}`, '_blank');
            }}>
              <Printer className="w-4 h-4 mr-1" />Boletim Oficial (PDF)
            </Button>
          </div>
        </div>
      </div>

      {/* ── Summary Cards ────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard icon={BarChart3} label="Media Geral" value={processedData.mediaGeral !== null ? fmtNota(processedData.mediaGeral) : '-'} color={processedData.mediaGeral >= 7 ? 'emerald' : processedData.mediaGeral >= 6 ? 'teal' : processedData.mediaGeral ? 'amber' : 'slate'} />
        <SummaryCard icon={AlertCircle} label="Total Faltas" value={processedData.totalFaltas} color={processedData.totalFaltas > 10 ? 'rose' : 'slate'} />
        <SummaryCard icon={Target} label="Situacao" value={processedData.situacaoGeral} color={processedData.situacaoGeral === 'Destaque' ? 'emerald' : processedData.situacaoGeral === 'Bom desempenho' ? 'teal' : 'blue'} small />
        <SummaryCard icon={BookOpen} label="Disciplinas" value={processedData.totalDisciplinas} color="purple" />
      </div>

      {/* ── Selo Motor Central ─────────────────────────── */}
      <div className="flex items-center justify-center" data-testid="motor-central-badge">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1">
          <Sparkles className="w-3 h-3" />
          Médias calculadas pelo Motor Central · arredondamento 0,5
        </span>
      </div>

      {/* ── Card Recuperação ─────────────────────────── */}
      {Array.isArray(boletim?.recuperacoes) && boletim.recuperacoes.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-4" data-testid="recuperacao-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-900 text-sm">Recuperação</h3>
            <span className="text-[10px] text-amber-500 ml-auto">{boletim.recuperacoes.length} registro{boletim.recuperacoes.length === 1 ? '' : 's'}</span>
          </div>
          <div className="space-y-2">
            {boletim.recuperacoes.map((r, i) => {
              const recuperado = r.situacao === 'Recuperado';
              return (
                <div key={i} className="bg-white/80 rounded-xl border border-amber-100 px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`recuperacao-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{r.subject_name || 'Disciplina'}</p>
                    <p className="text-[11px] text-slate-500">{r.period}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <div className="text-center"><div className="text-[10px] text-slate-400 uppercase">Média</div><div className="font-bold text-slate-700">{r.media_antes != null ? Number(r.media_antes).toFixed(1) : '—'}</div></div>
                    <div className="text-center"><div className="text-[10px] text-purple-400 uppercase">Recup.</div><div className="font-bold text-purple-700">{r.nota_recuperacao != null ? Number(r.nota_recuperacao).toFixed(1) : '—'}</div></div>
                    <div className="text-center"><div className="text-[10px] text-emerald-400 uppercase">Pós-Rec.</div><div className="font-bold text-emerald-700">{r.media_pos_rec != null ? Number(r.media_pos_rec).toFixed(1) : '—'}</div></div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${recuperado ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}
                      data-testid={`recuperacao-badge-${i}`}
                    >
                      {recuperado ? 'Recuperado' : 'Permanece'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── AI Insight Panel ────────────────────────────── */}
      {(aiInsight || aiLoading) && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-indigo-900 text-sm">Resumo Inteligente</h3>
            <span className="text-[10px] text-indigo-400 ml-auto">Interpretativo</span>
          </div>
          {aiLoading ? (
            <div className="flex items-center gap-2 text-sm text-indigo-400"><Loader2 className="w-4 h-4 animate-spin" />Analisando dados academicos...</div>
          ) : (
            <>
              <p className="text-sm text-indigo-800 leading-relaxed mb-4">{aiInsight?.resumo_geral}</p>
              {/* Strengths & Attention */}
              <div className="grid sm:grid-cols-2 gap-3">
                {aiInsight?.pontos_fortes?.length > 0 && (
                  <div className="bg-white/70 rounded-xl p-3 border border-emerald-100">
                    <p className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />Pontos Fortes</p>
                    <ul className="space-y-1">
                      {aiInsight.pontos_fortes.map((p, i) => <li key={i} className="text-xs text-emerald-800 flex items-start gap-1.5"><CheckCircle className="w-3 h-3 mt-0.5 text-emerald-500 shrink-0" />{p}</li>)}
                    </ul>
                  </div>
                )}
                {aiInsight?.pontos_atencao?.length > 0 && (
                  <div className="bg-white/70 rounded-xl p-3 border border-amber-100">
                    <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />Pontos de Atencao</p>
                    <ul className="space-y-1">
                      {aiInsight.pontos_atencao.map((p, i) => <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5"><Eye className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />{p}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              {aiInsight?.recomendacoes?.length > 0 && (
                <div className="mt-3 bg-white/70 rounded-xl p-3 border border-blue-100">
                  <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" />Recomendacoes</p>
                  <ul className="space-y-1">
                    {aiInsight.recomendacoes.map((r, i) => <li key={i} className="text-xs text-blue-800">{i + 1}. {r}</li>)}
                  </ul>
                </div>
              )}
              <p className="text-[10px] text-indigo-400 mt-3 italic">Este resumo e interpretativo e nao substitui os dados oficiais do boletim.</p>
            </>
          )}
        </div>
      )}

      {/* ── Desktop Table ────────────────────────────── */}
      <div className="hidden md:block">
        <Card className="border-slate-100 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10 min-w-[160px]">Disciplina</th>
                    <th className="text-center p-3 font-semibold text-slate-600 w-20">1T</th>
                    <th className="text-center p-3 font-semibold text-slate-600 w-20">2T</th>
                    <th className="text-center p-3 font-semibold text-slate-600 w-20">3T</th>
                    <th className="text-center p-3 font-semibold text-purple-700 bg-purple-50/50 w-24">Media Final</th>
                    <th className="text-center p-3 font-semibold text-slate-600 w-16">Faltas</th>
                    <th className="text-center p-3 font-semibold text-slate-600 w-28">Situacao</th>
                  </tr>
                </thead>
                <tbody>
                  {processedData.subjects.map((s, idx) => (
                    <tr key={s.id} className={`border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors ${idx % 2 === 0 ? '' : 'bg-slate-25'}`} onClick={() => setShowSubjectDetail(s)}>
                      <td className="p-3 font-medium text-slate-800 sticky left-0 bg-white z-10">{s.name}</td>
                      {['1o Trimestre', '2o Trimestre', '3o Trimestre'].map(t => (
                        <td key={t} className="p-3 text-center">
                          <span className={`font-bold ${getNotaClass(s.trims[t])}`}>{fmtNota(s.trims[t]) || <span className="text-slate-300">-</span>}</span>
                        </td>
                      ))}
                      <td className="p-3 text-center bg-purple-50/30">
                        <span className={`text-lg font-black ${getNotaClass(s.mediaFinal)}`}>{fmtNota(s.mediaFinal) || <span className="text-slate-300">-</span>}</span>
                      </td>
                      <td className="p-3 text-center text-slate-500">{s.faltas || 0}</td>
                      <td className="p-3 text-center">
                        <Badge className={`text-[10px] px-2 py-0.5 border ${getSituacaoBadge(s.situacao)}`}>{s.situacao}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Mobile Accordion ────────────────────────────── */}
      <div className="md:hidden space-y-2">
        {processedData.subjects.map(s => (
          <div key={s.id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <button onClick={() => toggleAccordion(s.id)} className="w-full flex items-center justify-between p-4 text-left">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{s.name}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-lg font-black ${getNotaClass(s.mediaParcial || s.mediaFinal)}`}>{fmtNota(s.mediaParcial || s.mediaFinal) || '-'}</span>
                  <Badge className={`text-[9px] px-1.5 py-0 border ${getSituacaoBadge(s.situacao)}`}>{s.situacao}</Badge>
                </div>
              </div>
              {expandedSubjects[s.id] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedSubjects[s.id] && (
              <div className="px-4 pb-4 border-t border-slate-50 pt-3 space-y-2">
                <div className="grid grid-cols-3 gap-2 text-center">
                  {['1o Trimestre', '2o Trimestre', '3o Trimestre'].map((t, i) => (
                    <div key={t} className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] text-slate-500 mb-1">{i + 1}o Trim</p>
                      <p className={`text-base font-bold ${getNotaClass(s.trims[t])}`}>{fmtNota(s.trims[t]) || '-'}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                  <span>Faltas: {s.faltas || 0}</span>
                  <span className={`font-semibold ${getNotaClass(s.mediaFinal)}`}>MF: {fmtNota(s.mediaFinal) || '-'}</span>
                </div>
                <Button size="sm" variant="outline" className="w-full text-xs mt-1" onClick={() => setShowSubjectDetail(s)}>
                  <Eye className="w-3 h-3 mr-1" />Ver detalhes
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Evolution Panel ────────────────────────────── */}
      {processedData.subjects.some(s => s.notasValidas.length >= 2) && (
        <Card className="border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-700"><BarChart3 className="w-4 h-4 text-indigo-500" />Evolucao Academica</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {processedData.subjects.filter(s => s.notasValidas.length >= 2).map(s => {
                const vals = ['1o Trimestre', '2o Trimestre', '3o Trimestre'].map(t => s.trims[t]).filter(v => v !== null && v !== undefined);
                const trend = vals.length >= 2 ? vals[vals.length - 1] - vals[vals.length - 2] : 0;
                const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
                const trendColor = trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-rose-600' : 'text-slate-400';
                return (
                  <div key={s.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <TrendIcon className={`w-5 h-5 ${trendColor} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{s.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {vals.map((v, i) => <span key={i} className={`text-[10px] font-bold ${getNotaClass(v)}`}>{fmtNota(v)}</span>)}
                      </div>
                    </div>
                    <span className={`text-xs font-bold ${trendColor}`}>{trend > 0 ? '+' : ''}{trend !== 0 ? trend.toFixed(1) : '='}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Legend ────────────────────────────── */}
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
        <div className="flex flex-wrap gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-full" />Aprovado/Destaque</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-teal-500 rounded-full" />Bom</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-500 rounded-full" />Atencao</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-rose-500 rounded-full" />Recuperacao</span>
          <span className="text-slate-400">* Media minima: 6,0 | MF = (1T+2T+3T)/3</span>
        </div>
      </div>

      {/* ── Subject Detail Modal ────────────────────────────── */}
      <Dialog open={!!showSubjectDetail && !assistantSubject} onOpenChange={() => setShowSubjectDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-purple-600" />{showSubjectDetail?.name}</DialogTitle>
            <DialogDescription>Detalhamento da disciplina</DialogDescription>
          </DialogHeader>
          {showSubjectDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-purple-50 rounded-xl">
                  <p className="text-[10px] text-purple-500 mb-1">Media</p>
                  <p className={`text-2xl font-black ${getNotaClass(showSubjectDetail.mediaParcial || showSubjectDetail.mediaFinal)}`}>{fmtNota(showSubjectDetail.mediaParcial || showSubjectDetail.mediaFinal) || '-'}</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-[10px] text-slate-500 mb-1">Faltas</p>
                  <p className="text-2xl font-black text-slate-700">{showSubjectDetail.faltas || 0}</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-[10px] text-slate-500 mb-1">Situacao</p>
                  <Badge className={`text-[10px] mt-1 border ${getSituacaoBadge(showSubjectDetail.situacao)}`}>{showSubjectDetail.situacao}</Badge>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600">Notas por Trimestre</p>
                {['1o Trimestre', '2o Trimestre', '3o Trimestre'].map((t, i) => {
                  const parciais = showSubjectDetail.trims[`${t}_parciais`] || {};
                  const nota = showSubjectDetail.trims[t];
                  return (
                    <div key={t} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-600">{i + 1}o Trimestre</span>
                        <span className={`text-sm font-bold ${getNotaClass(nota)}`}>{fmtNota(nota) || '-'}</span>
                      </div>
                      {Object.keys(parciais).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(parciais).map(([tipo, val]) => (
                            <span key={tipo} className="text-[10px] bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                              {tipo}: <strong className={getNotaClass(val)}>{fmtNota(val)}</strong>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* ── CTA Assistente da Família ────────────────────── */}
              <Button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md" onClick={() => openAssistant(showSubjectDetail)} data-testid="btn-family-assistant">
                <Sparkles className="w-4 h-4 mr-2" />Assistente da Familia
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Assistente da Família Modal ────────────────────────────── */}
      <Dialog open={!!assistantSubject} onOpenChange={() => setAssistantSubject(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
              <div>
                <span className="block text-sm">Assistente da Familia</span>
                <span className="block text-xs font-normal text-slate-500">{assistantSubject?.name}</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          {assistantLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-3" />
              <p className="text-sm text-slate-500">Gerando plano personalizado...</p>
              <p className="text-xs text-slate-400 mt-1">Analisando desempenho e alinhando a BNCC</p>
            </div>
          ) : assistantData ? (
            <div className="space-y-4">
              {/* Insight quick */}
              {assistantData.insight && (
                <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                  <p className="text-xs text-indigo-800 leading-relaxed">{assistantData.insight}</p>
                </div>
              )}

              <Tabs value={assistantTab} onValueChange={setAssistantTab}>
                <TabsList className="grid w-full grid-cols-3 h-9">
                  <TabsTrigger value="visao" className="text-xs"><Eye className="w-3 h-3 mr-1" />Visao Geral</TabsTrigger>
                  <TabsTrigger value="exercicios" className="text-xs"><PenTool className="w-3 h-3 mr-1" />Exercicios</TabsTrigger>
                  <TabsTrigger value="apoiar" className="text-xs"><Heart className="w-3 h-3 mr-1" />Como Apoiar</TabsTrigger>
                </TabsList>

                {/* Tab 1: Visão Geral */}
                <TabsContent value="visao" className="mt-3 space-y-3">
                  {assistantData.overview && (
                    <div className="p-4 bg-white rounded-xl border border-slate-100">
                      <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5 text-amber-500" />Orientacao para a Familia</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{assistantData.overview}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-3 bg-purple-50 rounded-xl">
                      <p className="text-[10px] text-purple-500 mb-1">Media</p>
                      <p className={`text-lg font-black ${getNotaClass(assistantSubject?.mediaParcial || assistantSubject?.mediaFinal)}`}>{fmtNota(assistantSubject?.mediaParcial || assistantSubject?.mediaFinal) || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-[10px] text-slate-500 mb-1">Faltas</p>
                      <p className="text-lg font-black text-slate-700">{assistantSubject?.faltas || 0}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-[10px] text-slate-500 mb-1">Situacao</p>
                      <Badge className={`text-[9px] border ${getSituacaoBadge(assistantSubject?.situacao)}`}>{assistantSubject?.situacao}</Badge>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 2: Exercícios */}
                <TabsContent value="exercicios" className="mt-3 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-slate-700 flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5 text-indigo-500" />Exercicios para Casa</p>
                    <Button size="sm" variant="ghost" className="text-xs h-7 text-slate-500" onClick={() => {
                      const txt = assistantData.exercises?.map((e, i) => `${i+1}. ${e.title}\n   ${e.description}\n   Dificuldade: ${e.difficulty}`).join('\n\n') || '';
                      const blob = new Blob([`Assistente da Familia - ${assistantSubject?.name}\n\n${txt}`], { type: 'text/plain' });
                      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `exercicios-${assistantSubject?.name}.txt`; a.click();
                    }}><Download className="w-3 h-3 mr-1" />Salvar</Button>
                  </div>
                  {assistantData.exercises?.map((ex, i) => (
                    <div key={i} className="p-3 bg-white rounded-xl border border-slate-100 hover:border-indigo-100 transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                          <p className="text-xs font-semibold text-slate-800">{ex.title}</p>
                        </div>
                        <Badge variant="outline" className={`text-[9px] shrink-0 ${ex.difficulty === 'facil' ? 'border-emerald-200 text-emerald-600' : ex.difficulty === 'desafio' ? 'border-amber-200 text-amber-600' : 'border-blue-200 text-blue-600'}`}>{ex.difficulty}</Badge>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed ml-7">{ex.description}</p>
                    </div>
                  ))}
                </TabsContent>

                {/* Tab 3: Como Apoiar */}
                <TabsContent value="apoiar" className="mt-3 space-y-3">
                  <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                    <p className="text-xs font-semibold text-emerald-800 mb-3 flex items-center gap-1.5"><Heart className="w-3.5 h-3.5 text-emerald-500" />Dicas para apoiar em casa</p>
                    <div className="space-y-2.5">
                      {assistantData.support?.map((tip, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <p className="text-sm text-emerald-800 leading-relaxed">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 italic text-center">Conteudo gerado pelo Assistente da Familia com base nos dados academicos oficiais e BNCC.</p>
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ── Legend Modal ────────────────────────────── */}
      <Dialog open={showLegend} onOpenChange={setShowLegend}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HelpCircle className="w-5 h-5 text-indigo-600" />Entenda as Siglas</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {SIGLAS.map(s => (
              <div key={s.sigla} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px] font-bold">{s.sigla}</Badge>
                  <span className="text-xs font-semibold text-slate-700">{s.nome}</span>
                </div>
                <p className="text-[11px] text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Summary Card Component ────────────────────────────────
function SummaryCard({ icon: Icon, label, value, color, small }) {
  const colorMap = {
    emerald: 'from-emerald-500 to-emerald-600 text-emerald-50',
    teal: 'from-teal-500 to-teal-600 text-teal-50',
    amber: 'from-amber-500 to-amber-600 text-amber-50',
    rose: 'from-rose-500 to-rose-600 text-rose-50',
    blue: 'from-blue-500 to-blue-600 text-blue-50',
    purple: 'from-purple-500 to-purple-600 text-purple-50',
    slate: 'from-slate-500 to-slate-600 text-slate-50',
  };
  return (
    <div className={`bg-gradient-to-br ${colorMap[color] || colorMap.slate} rounded-xl p-4 shadow-sm`}>
      <div className="flex items-center gap-1.5 mb-1 opacity-80">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className={`${small ? 'text-base' : 'text-2xl'} font-black`}>{value}</p>
    </div>
  );
}
